import {noop} from '@benchling/js-utils';
import React, {useMemo, useState} from 'react';
import * as _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS, TableViewsMetadata} from 'constants/AnalyticsConstants';
import {makeReactContext} from 'lib/makeReactContext';

import {StructuredTableOutsideNotebookProps} from '../../structured-tables/StructuredTableOutsideNotebook';
import {FocusedTableViewMode} from '../TableConstants';

export interface TableViewsContextValue {
  focusedStructuredTableCid: string | null;
  focusedTableViewMode: FocusedTableViewMode;
  tablesByCid: {[cid: string]: StructuredTableOutsideNotebookProps};
  provideStructuredTable: (tableProps: StructuredTableOutsideNotebookProps) => void;
  setTableView: (
    focusedStructuredTableCid: string | null,
    focusedTableViewMode?: FocusedTableViewMode
  ) => void;
}

function getDefaultTableViewsContext(): TableViewsContextValue {
  return {
    focusedStructuredTableCid: null,
    focusedTableViewMode: FocusedTableViewMode.FULLSCREEN,
    tablesByCid: {},
    provideStructuredTable: noop,
    setTableView: noop,
  };
}

export const TableViewsContext = makeReactContext<TableViewsContextValue>(
  getDefaultTableViewsContext,
  'TableViewsContext'
);

export const useTableViewsContext = (): TableViewsContextValue => TableViewsContext.useContext();

export const TableViewsWrapper = ({children}: {children: JSX.Element | null}): JSX.Element => {
  const [focusedStructuredTableCid, setFocusedStructuredTableCid] = useState<string | null>(null);
  const [focusedTableViewMode, setFocusedTableViewMode] = useState(FocusedTableViewMode.FULLSCREEN);
  const [tablesByCid, setTablesByCid] = useState<{[cid: string]: StructuredTableOutsideNotebookProps}>({});

  const context = useMemo(
    () => ({
      focusedStructuredTableCid,
      focusedTableViewMode,
      tablesByCid,
      provideStructuredTable: (tableProps: StructuredTableOutsideNotebookProps): void => {
        setTablesByCid((prevTablesByCid) => {
          if (!_.isMatch(prevTablesByCid[tableProps.cid], tableProps)) {
            return {
              ...prevTablesByCid,
              [tableProps.cid]: tableProps,
            };
          } else {
            return prevTablesByCid;
          }
        });
      },
      setTableView: (
        newFocusedStructuredTableCid: string | null,
        newFocusedTableViewMode: FocusedTableViewMode = focusedTableViewMode
      ): void => {
        if (
          focusedStructuredTableCid !== newFocusedStructuredTableCid ||
          focusedTableViewMode !== newFocusedTableViewMode
        ) {
          const analyticsData: TableViewsMetadata = {
            isMaximized: newFocusedStructuredTableCid != null,
            focusedTableViewMode: newFocusedTableViewMode,
          };
          if (newFocusedStructuredTableCid && tablesByCid[newFocusedStructuredTableCid]) {
            const tableProps = tablesByCid[newFocusedStructuredTableCid];
            analyticsData.structured_table_kind = tableProps.structuredTable.kind;
            analyticsData.structured_table_api_identifier = tableProps.structuredTable.apiId;
            analyticsData.structured_table_num_rows = tableProps.structuredTable.rows.length;
            analyticsData.structured_table_num_columns = tableProps.structuredTable.columns.length;
          }
          analytics.trackEvent(ELN_ANALYTICS_ACTIONS.CHANGED_TABLE_VIEW, analyticsData);
        }
        setFocusedStructuredTableCid(newFocusedStructuredTableCid);
        setFocusedTableViewMode(newFocusedTableViewMode);
      },
    }),
    [focusedStructuredTableCid, focusedTableViewMode, tablesByCid]
  );

  return (
    <TableViewsContext.Provider value={context}>
      <div style={{display: 'flex', height: '100%', width: '100%'}}>{children}</div>
    </TableViewsContext.Provider>
  );
};
